import { useState } from 'react'
import {
  Braces,
  ChevronDown,
  ChevronRight,
  FileCode2,
  FileJson2,
  FolderOpen,
  Frame,
  Layers3,
  MousePointer2,
  Type,
} from 'lucide-react'
import type { CanvasNode, StudioDocument } from '../core/model'

interface ExplorerPanelProps {
  document: StudioDocument
  selectedNodeId: string
  onSelect: (nodeId: string) => void
}

function nodeIcon(node: CanvasNode) {
  if (node.kind === 'text' || node.kind === 'badge') return <Type size={12} />
  if (node.kind === 'button') return <MousePointer2 size={12} />
  return <Frame size={12} />
}

function LayerRow({
  document,
  nodeId,
  selectedNodeId,
  depth,
  onSelect,
}: ExplorerPanelProps & { nodeId: string; depth: number }) {
  const node = document.nodes[nodeId]
  const [expanded, setExpanded] = useState(depth < 2)
  if (!node) return null
  const hasChildren = node.children.length > 0

  return (
    <>
      <div
        className={`layer-row ${selectedNodeId === nodeId ? 'is-selected' : ''}`}
        style={{ paddingLeft: 9 + depth * 13 }}
      >
        <button
          className="layer-chevron"
          type="button"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
          disabled={!hasChildren}
          onClick={() => setExpanded((value) => !value)}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown size={11} />
            ) : (
              <ChevronRight size={11} />
            )
          ) : null}
        </button>
        <button
          className="layer-select"
          type="button"
          onClick={() => onSelect(nodeId)}
        >
          {nodeIcon(node)}
          <span>{node.name}</span>
        </button>
      </div>
      {expanded
        ? node.children.map((childId) => (
            <LayerRow
              key={childId}
              document={document}
              selectedNodeId={selectedNodeId}
              onSelect={onSelect}
              nodeId={childId}
              depth={depth + 1}
            />
          ))
        : null}
    </>
  )
}

export function ExplorerPanel({
  document,
  selectedNodeId,
  onSelect,
}: ExplorerPanelProps) {
  const [tab, setTab] = useState<'files' | 'layers'>('layers')

  return (
    <aside className="explorer-panel">
      <div className="explorer-heading">
        <span>Explorer</span>
        <button type="button" aria-label="Explorer options">
          ···
        </button>
      </div>
      <div className="explorer-tabs" role="tablist" aria-label="Explorer view">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'layers'}
          className={tab === 'layers' ? 'is-active' : ''}
          onClick={() => setTab('layers')}
        >
          <Layers3 size={12} /> Layers
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'files'}
          className={tab === 'files' ? 'is-active' : ''}
          onClick={() => setTab('files')}
        >
          <Braces size={12} /> Files
        </button>
      </div>
      {tab === 'layers' ? (
        <div className="layer-tree">
          <div className="tree-section-label">
            <ChevronDown size={11} /> Canvas · {Object.keys(document.nodes).length}
          </div>
          <LayerRow
            document={document}
            selectedNodeId={selectedNodeId}
            onSelect={onSelect}
            nodeId={document.rootId}
            depth={0}
          />
        </div>
      ) : (
        <div className="file-tree">
          <div className="tree-section-label">
            <ChevronDown size={11} />
            <FolderOpen size={12} /> northstar-launch
          </div>
          <div className="file-row is-active">
            <FileCode2 size={12} /> App.tsx <span>M</span>
          </div>
          <div className="file-row">
            <FileCode2 size={12} /> tokens.css
          </div>
          <div className="file-row">
            <FileJson2 size={12} /> app.graph.json
          </div>
          <div className="tree-section-label nested">
            <ChevronRight size={11} />
            <FolderOpen size={12} /> runtime
          </div>
          <div className="tree-section-label nested">
            <ChevronRight size={11} />
            <FolderOpen size={12} /> components
          </div>
        </div>
      )}
      <div className="explorer-footer">
        <span className="presence-stack" aria-label="Three collaborators online">
          <i>M</i><i>J</i><i>+1</i>
        </span>
        <span>3 in canvas</span>
      </div>
    </aside>
  )
}
